/**
 * MCP Action Handlers
 *
 * Exposes safe, authenticated actions to window.__GETCLEARANCE__.actions
 * All actions go through the normal authenticated API - no shortcuts.
 *
 * SECURITY: Actions require an authenticated session.
 * They use the same auth token as the logged-in user.
 */

/**
 * Wrap an action with recording and error handling
 * Calls recordAction with {name, args, duration}, result, error
 */
function createActionWrapper(recordAction) {
  return function wrapAction(name, fn) {
    return async (...args) => {
      const start = Date.now();
      try {
        const result = await fn(...args);
        const duration = Date.now() - start;
        recordAction?.({ name, args: sanitizeArgs(args), duration }, result, null);
        return result;
      } catch (error) {
        const duration = Date.now() - start;
        recordAction?.({ name, args: sanitizeArgs(args), duration }, null, error);
        throw error;
      }
    };
  };
}

/**
 * Wrap a synchronous action (like navigate)
 */
function createSyncActionWrapper(recordAction) {
  return function wrapSyncAction(name, fn) {
    return (...args) => {
      const start = Date.now();
      try {
        const result = fn(...args);
        const duration = Date.now() - start;
        recordAction?.({ name, args: sanitizeArgs(args), duration }, result, null);
        return result;
      } catch (error) {
        const duration = Date.now() - start;
        recordAction?.({ name, args: sanitizeArgs(args), duration }, null, error);
        throw error;
      }
    };
  };
}

/**
 * Sanitize args to remove PII (emails, names, etc)
 */
function sanitizeArgs(args) {
  return args.map(arg => {
    if (typeof arg === 'string') {
      // Email pattern
      if (arg.includes('@') && arg.includes('.')) {
        return '[EMAIL]';
      }
      return arg;
    }
    if (arg && typeof arg === 'object') {
      const sanitized = {};
      for (const [key, value] of Object.entries(arg)) {
        if (key.toLowerCase().includes('email')) {
          sanitized[key] = '[EMAIL]';
        } else if (key.toLowerCase().includes('password') || key.toLowerCase().includes('token')) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof value === 'string' && value.includes('@') && value.includes('.')) {
          sanitized[key] = '[EMAIL]';
        } else {
          sanitized[key] = value;
        }
      }
      return sanitized;
    }
    return arg;
  });
}

export function createMCPActions({ navigate, queryClient, getToken, recordAction }) {
  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

  const wrapAction = createActionWrapper(recordAction);
  const wrapSyncAction = createSyncActionWrapper(recordAction);

  async function authenticatedFetch(path, options = {}) {
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `API error: ${response.status}`);
    }

    return response.json();
  }

  return {
    // ========================================
    // NAVIGATION
    // ========================================
    navigate: wrapSyncAction('navigate', (path) => {
      navigate(path);
      return { navigated: path };
    }),

    // ========================================
    // APPLICANT ACTIONS
    // ========================================
    approve: wrapAction('approve', async (id) => {
      const result = await authenticatedFetch(`/applicants/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: 'approved' }),
      });
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      return result;
    }),

    reject: wrapAction('reject', async (id, reason = '') => {
      const result = await authenticatedFetch(`/applicants/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: 'rejected', reason }),
      });
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      return result;
    }),

    createApplicant: wrapAction('createApplicant', async ({ email, firstName, lastName }) => {
      const result = await authenticatedFetch('/applicants', {
        method: 'POST',
        body: JSON.stringify({ email, first_name: firstName, last_name: lastName }),
      });
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      return { id: result.id, status: 'created' };
    }),

    batchApprove: wrapAction('batchApprove', async (ids) => {
      const results = await Promise.allSettled(
        ids.map(id => authenticatedFetch(`/applicants/${id}/status`, {
          method: 'POST',
          body: JSON.stringify({ status: 'approved' }),
        }))
      );
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      return {
        succeeded: results.filter(r => r.status === 'fulfilled').length,
        failed: results.filter(r => r.status === 'rejected').length,
        total: ids.length,
      };
    }),

    batchReject: wrapAction('batchReject', async (ids, reason = '') => {
      const results = await Promise.allSettled(
        ids.map(id => authenticatedFetch(`/applicants/${id}/status`, {
          method: 'POST',
          body: JSON.stringify({ status: 'rejected', reason }),
        }))
      );
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      return {
        succeeded: results.filter(r => r.status === 'fulfilled').length,
        failed: results.filter(r => r.status === 'rejected').length,
        total: ids.length,
      };
    }),

    addApplicantNote: wrapAction('addApplicantNote', async (id, content) => {
      const result = await authenticatedFetch(`/applicants/${id}/notes`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      });
      queryClient.invalidateQueries({ queryKey: ['applicants', id] });
      return { noteId: result.id, status: 'added' };
    }),

    requestDocuments: wrapAction('requestDocuments', async (id, types) => {
      await authenticatedFetch(`/applicants/${id}/request-documents`, {
        method: 'POST',
        body: JSON.stringify({ document_types: types }),
      });
      queryClient.invalidateQueries({ queryKey: ['applicants', id] });
      return { requested: types, status: 'sent' };
    }),

    reVerifyApplicant: wrapAction('reVerifyApplicant', async (id) => {
      await authenticatedFetch(`/applicants/${id}/reverify`, {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: ['applicants', id] });
      return { status: 'reverification_started' };
    }),

    downloadEvidence: wrapAction('downloadEvidence', async (id) => {
      // Returns metadata about download, actual file is downloaded via browser
      const result = await authenticatedFetch(`/applicants/${id}/evidence-pack`);
      return { downloadUrl: result.url, expiresIn: result.expires_in };
    }),

    shareApplicant: wrapAction('shareApplicant', async (id) => {
      const result = await authenticatedFetch(`/applicants/${id}/share`, {
        method: 'POST',
      });
      return { shareId: result.id, shareUrl: result.url };
    }),

    exportApplicants: wrapAction('exportApplicants', async (filters = {}) => {
      const params = new URLSearchParams(filters).toString();
      const result = await authenticatedFetch(`/applicants/export${params ? '?' + params : ''}`);
      return { downloadUrl: result.url, recordCount: result.count };
    }),

    // ========================================
    // CASE ACTIONS
    // ========================================
    createCase: wrapAction('createCase', async ({ applicantId, caseType, description }) => {
      const result = await authenticatedFetch('/cases', {
        method: 'POST',
        body: JSON.stringify({
          applicant_id: applicantId,
          case_type: caseType,
          description,
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      return { id: result.id, status: 'created' };
    }),

    resolveCase: wrapAction('resolveCase', async (id, resolution, notes = '') => {
      await authenticatedFetch(`/cases/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ resolution, notes }),
      });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      return { id, resolution, status: 'resolved' };
    }),

    updateCaseStatus: wrapAction('updateCaseStatus', async (id, status) => {
      await authenticatedFetch(`/cases/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      return { id, status };
    }),

    addCaseNote: wrapAction('addCaseNote', async (id, content) => {
      const result = await authenticatedFetch(`/cases/${id}/notes`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      });
      queryClient.invalidateQueries({ queryKey: ['cases', id] });
      return { noteId: result.id, status: 'added' };
    }),

    escalateCase: wrapAction('escalateCase', async (id, priority, reason) => {
      await authenticatedFetch(`/cases/${id}/escalate`, {
        method: 'POST',
        body: JSON.stringify({ priority, reason }),
      });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      return { id, priority, status: 'escalated' };
    }),

    createSAR: wrapAction('createSAR', async (caseId, data) => {
      const result = await authenticatedFetch(`/cases/${caseId}/sar`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      queryClient.invalidateQueries({ queryKey: ['cases', caseId] });
      return { sarId: result.id, status: 'created' };
    }),

    // ========================================
    // SCREENING ACTIONS
    // ========================================
    screen: wrapAction('screen', async (id) => {
      const result = await authenticatedFetch(`/screening/run/${id}`, {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      queryClient.invalidateQueries({ queryKey: ['screening'] });
      return result;
    }),

    runScreening: wrapAction('runScreening', async (applicantId, checkTypes = []) => {
      const result = await authenticatedFetch(`/screening/run/${applicantId}`, {
        method: 'POST',
        body: JSON.stringify({ check_types: checkTypes }),
      });
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
      queryClient.invalidateQueries({ queryKey: ['screening'] });
      return result;
    }),

    runNewScreeningCheck: wrapAction('runNewScreeningCheck', async ({ name, country, dob }) => {
      const result = await authenticatedFetch('/screening/check', {
        method: 'POST',
        body: JSON.stringify({ name, country, date_of_birth: dob }),
      });
      queryClient.invalidateQueries({ queryKey: ['screening'] });
      return result;
    }),

    resolveScreeningHit: wrapAction('resolveScreeningHit', async (hitId, resolution, notes = '') => {
      await authenticatedFetch(`/screening/hits/${hitId}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ resolution, notes }), // resolution: 'clear' | 'confirmed'
      });
      queryClient.invalidateQueries({ queryKey: ['screening'] });
      return { hitId, resolution, status: 'resolved' };
    }),

    getAISuggestion: wrapAction('getAISuggestion', async (hitId) => {
      const result = await authenticatedFetch(`/screening/hits/${hitId}/ai-suggestion`);
      return result;
    }),

    refreshScreeningSources: wrapAction('refreshScreeningSources', async () => {
      const result = await authenticatedFetch('/screening/sources/refresh', {
        method: 'POST',
      });
      return { status: 'refreshed', sources: result.sources };
    }),

    // ========================================
    // WORKFLOW ACTIONS
    // ========================================
    createWorkflow: wrapAction('createWorkflow', async ({ name }) => {
      const result = await authenticatedFetch('/workflow-builder/workflows', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      return { id: result.id, status: 'created' };
    }),

    createWorkflowFromPrompt: wrapAction('createWorkflowFromPrompt', async ({ name, prompt }) => {
      const result = await authenticatedFetch('/workflow-builder/workflows/ai/generate', {
        method: 'POST',
        body: JSON.stringify({ name, prompt }),
      });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      return { id: result.id, nodeCount: result.nodes?.length, status: 'created' };
    }),

    addWorkflowNode: wrapAction('addWorkflowNode', async (workflowId, componentId, position = null) => {
      const result = await authenticatedFetch(`/workflow-builder/workflows/${workflowId}/nodes`, {
        method: 'POST',
        body: JSON.stringify({ component_id: componentId, position }),
      });
      queryClient.invalidateQueries({ queryKey: ['workflows', workflowId] });
      return { nodeId: result.id, status: 'added' };
    }),

    removeWorkflowNode: wrapAction('removeWorkflowNode', async (workflowId, nodeId) => {
      await authenticatedFetch(`/workflow-builder/workflows/${workflowId}/nodes/${nodeId}`, {
        method: 'DELETE',
      });
      queryClient.invalidateQueries({ queryKey: ['workflows', workflowId] });
      return { nodeId, status: 'removed' };
    }),

    connectNodes: wrapAction('connectNodes', async (workflowId, sourceId, targetId, condition = null) => {
      const result = await authenticatedFetch(`/workflow-builder/workflows/${workflowId}/edges`, {
        method: 'POST',
        body: JSON.stringify({ source_id: sourceId, target_id: targetId, condition }),
      });
      queryClient.invalidateQueries({ queryKey: ['workflows', workflowId] });
      return { edgeId: result.id, status: 'connected' };
    }),

    workflowChat: wrapAction('workflowChat', async (workflowId, message) => {
      const result = await authenticatedFetch(`/workflow-builder/workflows/${workflowId}/chat`, {
        method: 'POST',
        body: JSON.stringify({ message }),
      });
      queryClient.invalidateQueries({ queryKey: ['workflows', workflowId] });
      return result;
    }),

    saveWorkflow: wrapAction('saveWorkflow', async (workflowId) => {
      const result = await authenticatedFetch(`/workflow-builder/workflows/${workflowId}/save`, {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: ['workflows', workflowId] });
      return { status: 'saved', version: result.version };
    }),

    publishWorkflow: wrapAction('publishWorkflow', async (workflowId) => {
      const result = await authenticatedFetch(`/workflow-builder/workflows/${workflowId}/publish`, {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      return { status: 'published', version: result.version };
    }),

    rollbackWorkflow: wrapAction('rollbackWorkflow', async (workflowId, versionId) => {
      await authenticatedFetch(`/workflow-builder/workflows/${workflowId}/rollback`, {
        method: 'POST',
        body: JSON.stringify({ version_id: versionId }),
      });
      queryClient.invalidateQueries({ queryKey: ['workflows', workflowId] });
      return { status: 'rolledBack', version: versionId };
    }),

    deleteWorkflow: wrapAction('deleteWorkflow', async (workflowId) => {
      await authenticatedFetch(`/workflow-builder/workflows/${workflowId}`, {
        method: 'DELETE',
      });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      return { status: 'deleted' };
    }),

    validateWorkflow: wrapAction('validateWorkflow', async (workflowId) => {
      const result = await authenticatedFetch(`/workflow-builder/workflows/${workflowId}/validate`);
      return result;
    }),

    // ========================================
    // COMPANIES (KYB) ACTIONS
    // ========================================
    createCompany: wrapAction('createCompany', async ({ legalName, country, registrationNumber }) => {
      const result = await authenticatedFetch('/companies', {
        method: 'POST',
        body: JSON.stringify({
          legal_name: legalName,
          country,
          registration_number: registrationNumber,
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      return { id: result.id, status: 'created' };
    }),

    screenCompany: wrapAction('screenCompany', async (companyId) => {
      const result = await authenticatedFetch(`/companies/${companyId}/screen`, {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: ['companies', companyId] });
      return result;
    }),

    approveCompany: wrapAction('approveCompany', async (companyId) => {
      await authenticatedFetch(`/companies/${companyId}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: 'approved' }),
      });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      return { id: companyId, status: 'approved' };
    }),

    rejectCompany: wrapAction('rejectCompany', async (companyId, reason = '') => {
      await authenticatedFetch(`/companies/${companyId}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: 'rejected', reason }),
      });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      return { id: companyId, status: 'rejected' };
    }),

    addUBODocument: wrapAction('addUBODocument', async (companyId, uboId, docType) => {
      const result = await authenticatedFetch(`/companies/${companyId}/ubos/${uboId}/documents`, {
        method: 'POST',
        body: JSON.stringify({ document_type: docType }),
      });
      queryClient.invalidateQueries({ queryKey: ['companies', companyId] });
      return { documentId: result.id, status: 'added' };
    }),

    requestCompanyDocs: wrapAction('requestCompanyDocs', async (companyId, types) => {
      await authenticatedFetch(`/companies/${companyId}/request-documents`, {
        method: 'POST',
        body: JSON.stringify({ document_types: types }),
      });
      queryClient.invalidateQueries({ queryKey: ['companies', companyId] });
      return { requested: types, status: 'sent' };
    }),

    exportCompanies: wrapAction('exportCompanies', async (filters = {}) => {
      const params = new URLSearchParams(filters).toString();
      const result = await authenticatedFetch(`/companies/export${params ? '?' + params : ''}`);
      return { downloadUrl: result.url, recordCount: result.count };
    }),

    // ========================================
    // BILLING ACTIONS
    // ========================================
    openBillingPortal: wrapAction('openBillingPortal', async () => {
      const result = await authenticatedFetch('/billing/portal-session', {
        method: 'POST',
      });
      window.open(result.url, '_blank');
      return { status: 'opened' };
    }),

    downloadInvoice: wrapAction('downloadInvoice', async (invoiceId) => {
      const result = await authenticatedFetch(`/billing/invoices/${invoiceId}/download`);
      return { downloadUrl: result.url };
    }),

    switchBillingTab: wrapSyncAction('switchBillingTab', (tabName) => {
      window.dispatchEvent(new CustomEvent('mcp:billingTab', { detail: { tab: tabName } }));
      return { tab: tabName };
    }),

    // ========================================
    // SETTINGS ACTIONS
    // ========================================
    updateSettings: wrapAction('updateSettings', async (section, data) => {
      await authenticatedFetch(`/settings/${section}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      return { section, status: 'updated' };
    }),

    resetSettings: wrapAction('resetSettings', async (section) => {
      await authenticatedFetch(`/settings/${section}/reset`, {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      return { section, status: 'reset' };
    }),

    exportSettings: wrapAction('exportSettings', async () => {
      const result = await authenticatedFetch('/settings/export');
      return { downloadUrl: result.url };
    }),

    // ========================================
    // AUDIT LOG ACTIONS
    // ========================================
    filterAuditLog: wrapSyncAction('filterAuditLog', (filters) => {
      window.dispatchEvent(new CustomEvent('mcp:auditFilter', { detail: filters }));
      queryClient.invalidateQueries({ queryKey: ['audit-log'] });
      return { filters, status: 'applied' };
    }),

    exportAuditLog: wrapAction('exportAuditLog', async (filters = {}) => {
      const params = new URLSearchParams(filters).toString();
      const result = await authenticatedFetch(`/audit-log/export${params ? '?' + params : ''}`);
      return { downloadUrl: result.url, recordCount: result.count };
    }),

    searchAuditLog: wrapSyncAction('searchAuditLog', (query) => {
      window.dispatchEvent(new CustomEvent('mcp:auditSearch', { detail: { query } }));
      return { query, status: 'searching' };
    }),

    // ========================================
    // QUESTIONNAIRE ACTIONS
    // ========================================
    createQuestionnaire: wrapAction('createQuestionnaire', async ({ name, type }) => {
      const result = await authenticatedFetch('/questionnaires', {
        method: 'POST',
        body: JSON.stringify({ name, type }),
      });
      queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
      return { id: result.id, status: 'created' };
    }),

    editQuestionnaire: wrapAction('editQuestionnaire', async (id, data) => {
      await authenticatedFetch(`/questionnaires/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      queryClient.invalidateQueries({ queryKey: ['questionnaires', id] });
      return { id, status: 'updated' };
    }),

    deleteQuestionnaire: wrapAction('deleteQuestionnaire', async (id) => {
      await authenticatedFetch(`/questionnaires/${id}`, {
        method: 'DELETE',
      });
      queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
      return { id, status: 'deleted' };
    }),

    previewQuestionnaire: wrapSyncAction('previewQuestionnaire', (id) => {
      window.open(`/questionnaires/${id}/preview`, '_blank');
      return { id, status: 'previewing' };
    }),

    sendQuestionnaire: wrapAction('sendQuestionnaire', async (questionnaireId, applicantId) => {
      await authenticatedFetch(`/questionnaires/${questionnaireId}/send`, {
        method: 'POST',
        body: JSON.stringify({ applicant_id: applicantId }),
      });
      return { sent: true, applicantId };
    }),

    // ========================================
    // BIOMETRICS ACTIONS
    // ========================================
    viewBiometricReport: wrapAction('viewBiometricReport', async (applicantId) => {
      const result = await authenticatedFetch(`/biometrics/${applicantId}/report`);
      return result;
    }),

    reRunLiveness: wrapAction('reRunLiveness', async (applicantId) => {
      await authenticatedFetch(`/biometrics/${applicantId}/liveness/rerun`, {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: ['applicants', applicantId] });
      return { status: 'requested' };
    }),

    exportBiometricData: wrapAction('exportBiometricData', async (applicantId) => {
      const result = await authenticatedFetch(`/biometrics/${applicantId}/export`);
      return { downloadUrl: result.url };
    }),

    // ========================================
    // ANALYTICS ACTIONS
    // ========================================
    setDateRange: wrapSyncAction('setDateRange', (start, end) => {
      window.dispatchEvent(new CustomEvent('mcp:dateRange', { detail: { start, end } }));
      return { start, end, status: 'applied' };
    }),

    exportAnalytics: wrapAction('exportAnalytics', async (reportType) => {
      const result = await authenticatedFetch(`/analytics/export?report=${reportType}`);
      return { downloadUrl: result.url, reportType };
    }),

    // ========================================
    // INTEGRATIONS ACTIONS
    // ========================================
    connectIntegration: wrapAction('connectIntegration', async (integrationId) => {
      const result = await authenticatedFetch(`/integrations/${integrationId}/connect`, {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      // If OAuth, this returns a URL to redirect to
      if (result.redirect_url) {
        window.open(result.redirect_url, '_blank');
      }
      return { id: integrationId, status: result.status || 'connecting' };
    }),

    disconnectIntegration: wrapAction('disconnectIntegration', async (integrationId) => {
      await authenticatedFetch(`/integrations/${integrationId}/disconnect`, {
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      return { id: integrationId, status: 'disconnected' };
    }),

    testIntegration: wrapAction('testIntegration', async (integrationId) => {
      const result = await authenticatedFetch(`/integrations/${integrationId}/test`, {
        method: 'POST',
      });
      return { id: integrationId, ...result };
    }),

    configureWebhook: wrapAction('configureWebhook', async (integrationId, url, events) => {
      await authenticatedFetch(`/integrations/${integrationId}/webhook`, {
        method: 'PUT',
        body: JSON.stringify({ url, events }),
      });
      queryClient.invalidateQueries({ queryKey: ['integrations', integrationId] });
      return { id: integrationId, status: 'configured' };
    }),

    // ========================================
    // REUSABLE KYC ACTIONS
    // ========================================
    createKYCShare: wrapAction('createKYCShare', async (applicantId, options = {}) => {
      const result = await authenticatedFetch(`/applicants/${applicantId}/share`, {
        method: 'POST',
        body: JSON.stringify(options),
      });
      return { shareId: result.id, shareUrl: result.url };
    }),

    revokeKYCShare: wrapAction('revokeKYCShare', async (shareId) => {
      await authenticatedFetch(`/kyc-shares/${shareId}`, {
        method: 'DELETE',
      });
      queryClient.invalidateQueries({ queryKey: ['kyc-shares'] });
      return { shareId, status: 'revoked' };
    }),

    exportKYCResponses: wrapAction('exportKYCResponses', async (shareId) => {
      const result = await authenticatedFetch(`/kyc-shares/${shareId}/export`);
      return { downloadUrl: result.url };
    }),

    // ========================================
    // FILTERING & UI (legacy, still supported)
    // ========================================
    filter: wrapSyncAction('filter', (field, value) => {
      window.dispatchEvent(
        new CustomEvent('mcp:filter', { detail: { field, value } })
      );
      return { field, value };
    }),

    search: wrapSyncAction('search', (query) => {
      window.dispatchEvent(
        new CustomEvent('mcp:search', { detail: { query } })
      );
      return { query };
    }),

    select: wrapSyncAction('select', (ids) => {
      window.dispatchEvent(
        new CustomEvent('mcp:select', { detail: { ids } })
      );
      return { ids };
    }),

    // ========================================
    // CACHE MANAGEMENT
    // ========================================
    refetch: wrapSyncAction('refetch', (queryKey) => {
      if (queryKey) {
        queryClient.invalidateQueries({ queryKey: [queryKey] });
      } else {
        queryClient.invalidateQueries();
      }
      return { queryKey: queryKey || 'all' };
    }),
  };
}
